import type { ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAdminSection } from '@/lib/adminSections';
import { useLanguage } from '@/contexts/LanguageContext';

/*
 * The redesign's building blocks for the family, teacher and admin pages
 * (DESIGN.md, boards f*, t*, a*): pill buttons, soft cards, lime as the fill
 * for what is selected or earned, and an empty state that is a lime disc,
 * one line and at most one action. Colours are theme names only, so each
 * piece reads correctly on the paper and the night palettes alike.
 */

/** Pill buttons. Heights follow DESIGN.md: 54 for a page's main action, 44 inside cards. */
export const pill = {
  base: 'inline-flex items-center justify-center gap-2 rounded-full px-5 font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-ground',
  /** The page's one main action: teal on paper, lime on night. */
  primary: 'bg-primary text-primary-foreground hover:bg-primary-dark',
  /** Secondary action and "selected": lime fill, ink label; a softer lime on night. */
  lime: 'bg-lime text-accent-foreground hover:bg-lime/85 dark:bg-lime/15 dark:text-lime dark:hover:bg-lime/25',
  /** A quiet choice next to a filled one. */
  quiet: 'border border-border-strong/70 bg-transparent text-ink hover:bg-surface',
  /** Destructive, before it is confirmed: red outline. */
  dangerOutline: 'border border-danger bg-transparent text-danger hover:bg-danger/10',
  /** Destructive, the confirming press. */
  danger: 'bg-danger text-primary-foreground hover:bg-danger/90',
  lg: 'h-[54px] text-base',
  md: 'h-11 text-[15px]',
  sm: 'h-9 px-4 text-sm',
};

/** Text fields: radius 14, a quiet edge that turns primary on focus. */
export const field = 'w-full rounded-[14px] border border-border-strong/50 bg-card px-4 py-3 text-ink placeholder:text-helper/70 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/15';

/** A page title, its line of context, and an optional way back. */
export function PageHeader({ title, subtitle, back, actions }: {
  title: ReactNode;
  subtitle?: ReactNode;
  back?: { to: string; label: string; testId?: string };
  actions?: ReactNode;
}) {
  const section = useAdminSection();
  const { t } = useLanguage();

  // A supervisor page (#12): the header IS the category's hero — the page's
  // title and line, its actions in lime, and the category's pages as pills.
  // A detail page with a way back keeps the plain header.
  if (section && !back) {
    return (
      <header data-testid="admin-hero" className="hero-card px-6 py-6 lg:px-8 lg:py-7">
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-[28px] font-semibold leading-tight">{title}</h1>
            {subtitle && <p className="mt-1 text-[15px] text-[rgb(var(--hero-sub))]">{subtitle}</p>}
          </div>
          {actions && <div className="hero-actions flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
        {section.pages.length > 1 && (
          <nav
            aria-label={t('admin.section.pages', { name: t(section.labelKey) })}
            className="relative mt-5 inline-flex max-w-full flex-wrap gap-1 rounded-full bg-white/15 p-1 dark:bg-white/[0.08]"
          >
            {section.pages.map((page) => (
              <NavLink
                key={page.to}
                to={page.to}
                data-testid={page.testId}
                className={({ isActive }) => cn(
                  'inline-flex h-10 items-center rounded-full px-5 text-[15px] transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70',
                  isActive
                    ? 'bg-white font-medium text-primary dark:bg-lime dark:text-accent-foreground'
                    : 'text-[rgb(var(--hero-text))] hover:bg-white/10',
                )}
              >
                {t(page.labelKey)}
              </NavLink>
            ))}
          </nav>
        )}
      </header>
    );
  }

  return (
    <header className="flex flex-wrap items-center justify-between gap-4">
      <div className="flex min-w-0 items-center gap-3">
        {back && (
          // Points the reading way: right in Arabic, left in English.
          <Link
            to={back.to}
            data-testid={back.testId}
            aria-label={back.label}
            title={back.label}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface text-ink transition-colors hover:bg-tint"
          >
            <ArrowRight className="h-5 w-5 ltr:rotate-180" />
          </Link>
        )}
        <div className="min-w-0">
          <h1 className="truncate text-[26px] font-semibold leading-tight text-ink">{title}</h1>
          {subtitle && <p className="mt-1 text-[15px] text-helper">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

/** A soft card: shadow on paper, a faint lift on night — never a boxed border. */
export function SoftCard({ children, className, testId, as: Tag = 'section' }: {
  children: ReactNode;
  className?: string;
  testId?: string;
  as?: 'section' | 'div' | 'li' | 'article';
}) {
  return <Tag data-testid={testId} className={cn('card-soft p-5', className)}>{children}</Tag>;
}

/** A card's heading row: an icon tile, the title, and something at the end. */
export function CardTitle({ icon, children, trailing, level = 2 }: {
  icon?: ReactNode;
  children: ReactNode;
  trailing?: ReactNode;
  level?: 2 | 3;
}) {
  const H = level === 2 ? 'h2' : 'h3';
  return (
    <div className="flex items-center justify-between gap-3">
      <H className="flex min-w-0 items-center gap-3 text-lg font-semibold text-ink">
        {icon && <IconTile>{icon}</IconTile>}
        <span className="min-w-0">{children}</span>
      </H>
      {trailing}
    </div>
  );
}

/** The small rounded square on tint that carries a row's or card's icon. */
export function IconTile({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-[14px] bg-tint text-primary [&>svg]:h-5 [&>svg]:w-5', className)}>
      {children}
    </span>
  );
}

/** Every empty state: a lime disc, one line of why, and at most one real action. */
export function EmptyState({ testId, icon, title, body, action, className }: {
  testId?: string;
  icon: ReactNode;
  title: string;
  body?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div data-testid={testId} className={cn('flex flex-col items-center gap-2 px-4 py-8 text-center', className)}>
      <span className="mb-2 flex h-16 w-16 items-center justify-center rounded-full bg-lime text-accent-foreground [&>svg]:h-7 [&>svg]:w-7">
        {icon}
      </span>
      <p className="text-lg font-semibold text-ink">{title}</p>
      {body && <p className="max-w-md text-[15px] text-helper">{body}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

/** A short status in a lime pill: «مباشر», «منشور», a count. */
export function LimePill({ children, testId, className }: { children: ReactNode; testId?: string; className?: string }) {
  return (
    <span data-testid={testId} className={cn('inline-flex shrink-0 items-center gap-1 rounded-full bg-lime px-3 py-1 text-xs font-semibold text-accent-foreground', className)}>
      {children}
    </span>
  );
}

/** A quiet status pill: draft, closed, followed only. */
export function QuietPill({ children, testId, className }: { children: ReactNode; testId?: string; className?: string }) {
  return (
    <span data-testid={testId} className={cn('inline-flex shrink-0 items-center gap-1 rounded-full bg-surface px-3 py-1 text-xs font-medium text-helper', className)}>
      {children}
    </span>
  );
}

/** A message in the flow: an error, or a quiet "done". */
export function Notice({ tone, testId, children, className }: {
  tone: 'danger' | 'ok';
  testId?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <p
      data-testid={testId}
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn(
        'rounded-[14px] px-4 py-3 text-sm',
        tone === 'danger' ? 'bg-danger/10 text-danger' : 'bg-tint text-ink',
        className,
      )}
    >
      {children}
    </p>
  );
}

/**
 * A segmented filter: one pill track, the chosen segment filled. Buttons with
 * aria-pressed rather than tabs, because each one filters the SAME list.
 */
export function Segmented<T extends string>({ label, value, options, onChange, className }: {
  label: string;
  value: T;
  options: { value: T; label: ReactNode; testId?: string }[];
  onChange: (next: T) => void;
  className?: string;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cn('inline-flex max-w-full flex-wrap gap-1 rounded-full bg-card p-1 shadow-sm ring-1 ring-hair', className)}
    >
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            data-testid={o.testId}
            aria-pressed={on}
            onClick={() => onChange(o.value)}
            className={cn(
              'inline-flex h-9 items-center gap-1.5 rounded-full px-4 text-sm font-medium transition-colors',
              on ? 'bg-primary text-primary-foreground' : 'text-helper hover:bg-surface hover:text-ink',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** A number with its label underneath, on tint — the week, a count, a share. */
export function StatTile({ testId, value, label, className }: {
  testId?: string;
  value: ReactNode;
  label: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('rounded-[18px] bg-tint px-4 py-4', className)}>
      <p data-testid={testId} className="text-[28px] font-semibold leading-none text-ink">{value}</p>
      <p className="mt-2 text-sm text-helper">{label}</p>
    </div>
  );
}

/** Two choices side by side — a gender, a mode — the chosen one in lime. */
export function ChoicePills<T extends string>({ value, options, onChange, className }: {
  value: T | null;
  options: { value: T; label: ReactNode; testId?: string }[];
  onChange: (next: T) => void;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            data-testid={o.testId}
            aria-pressed={on}
            onClick={() => onChange(o.value)}
            className={cn(
              pill.base, pill.md, 'min-w-[96px] flex-1',
              on ? 'bg-lime text-accent-foreground' : pill.quiet,
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
